// src/tabs/PrecoNovosProdutosSubTab.js
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Plus, Trash2, HelpCircle, Save, Database, ShieldAlert, Sparkles, Layers } from "lucide-react";
import { pb } from "../services/pocketbase";
import { suppliesService } from "../services/suppliesService";

const formatBRL = (val) => {
  return Number(val || 0).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const getSupplyUnitCost = (supply) => {
  return suppliesService.getSupplyUnitCost(supply);
};

export default function PrecoNovosProdutosSubTab({ user, findCatalogItem }) {
  const [loading, setLoading] = useState(true);
  const [sku, setSku] = useState("");
  const [produtoNome, setProdutoNome] = useState("");
  const [modelo, setModelo] = useState("");
  const [sourceModelingId, setSourceModelingId] = useState("");
  const [loadedSku, setLoadedSku] = useState("");
  const [custoOperacional, setCustoOperacional] = useState("10.00");
  const [indiceOperacional, setIndiceOperacional] = useState("1.5");
  const [indiceFinanceiro, setIndiceFinanceiro] = useState("1.2");

  // Default multipliers loaded from localStorage
  const [multPadraoMetal, setMultPadraoMetal] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("calc_mult_padrao_metal") || "4.0";
    }
    return "4.0";
  });
  const [multPadraoPedra, setMultPadraoPedra] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("calc_mult_padrao_pedra") || "2.0";
    }
    return "2.0";
  });
  const [multPadraoInsumo, setMultPadraoInsumo] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("calc_mult_padrao_insumo") || "2.0";
    }
    return "2.0";
  });

  const [ligas, setLigas] = useState([]);
  const [supplies, setSupplies] = useState([]);
  const [bomItems, setBomItems] = useState([]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("calc_mult_padrao_metal", multPadraoMetal);
    }
  }, [multPadraoMetal]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("calc_mult_padrao_pedra", multPadraoPedra);
    }
  }, [multPadraoPedra]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("calc_mult_padrao_insumo", multPadraoInsumo);
    }
  }, [multPadraoInsumo]);

  // Metal Modal States
  const [isMetalModalOpen, setIsMetalModalOpen] = useState(false);
  const [metalModalForm, setMetalModalForm] = useState({
    liga_id: "",
    peso: "1.0",
    customNome: "Metal Customizado",
    customCusto: "0",
    customMult: "4.0"
  });

  // Recipe/BOM Template Modal States
  const [templates, setTemplates] = useState({});
  const [isRecipeModalOpen, setIsRecipeModalOpen] = useState(false);
  const [selectedRecipeName, setSelectedRecipeName] = useState("");
  const [clearExistingOnApply, setClearExistingOnApply] = useState(true);

  const handleApplyRecipeTemplate = (e) => {
    e.preventDefault();
    if (!selectedRecipeName) return alert("Selecione uma receita.");

    const templateItems = templates[selectedRecipeName];
    if (!templateItems || templateItems.length === 0) return alert("Esta receita está vazia.");

    const newItems = templateItems.map(item => {
      const supply = supplies.find(s => s.id === item.supply_id);
      if (!supply) return null;

      const isStone = supply.sector === "pedras";
      const isMetal = supply.category === "metal" || (supply.sector === "oficina" && (supply.name.includes("Ouro") || supply.name.includes("Prata")));

      if (isMetal) {
        return {
          id: Date.now().toString() + "-" + supply.id + "-" + Math.random().toString(36).substring(2, 5),
          type: "metal",
          supply_id: supply.id,
          nome: supply.name,
          peso: item.quantity_per_unit || 1.0,
          custo_unitario: getSupplyUnitCost(supply),
          multiplicador: parseFloat(multPadraoMetal) || 4.0
        };
      }

      return {
        id: Date.now().toString() + "-" + supply.id + "-" + Math.random().toString(36).substring(2, 5),
        type: isStone ? "stone" : "insumo",
        supply_id: supply.id,
        nome: supply.name,
        quantidade: item.quantity_per_unit || 1,
        custo_unitario: getSupplyUnitCost(supply),
        multiplicador: parseFloat(isStone ? multPadraoPedra : multPadraoInsumo) || 2.0
      };
    }).filter(Boolean);

    setBomItems(prev => clearExistingOnApply ? newItems : [...prev, ...newItems]);
    setIsRecipeModalOpen(false);
    alert(`Receita "${selectedRecipeName}" aplicada com sucesso!`);
  };

  const handleOpenMetalModal = () => {
    const defaultLiga = ligas.length > 0 ? ligas[0].id : "custom";
    setMetalModalForm({
      liga_id: defaultLiga,
      peso: "1.0",
      customNome: "Metal Customizado",
      customCusto: "0",
      customMult: multPadraoMetal
    });
    setIsMetalModalOpen(true);
  };

  const handleConfirmMetalModal = (e) => {
    e.preventDefault();
    const pesoTotal = parseFloat(metalModalForm.peso) || 0;
    if (pesoTotal <= 0) return alert("Digite um peso válido.");

    if (metalModalForm.liga_id === "custom") {
      setBomItems(prev => [
        ...prev,
        {
          id: Date.now().toString(),
          type: "metal",
          nome: metalModalForm.customNome.trim(),
          liga_id: "custom",
          peso: pesoTotal,
          multiplicador: parseFloat(metalModalForm.customMult) || parseFloat(multPadraoMetal) || 4.0,
          custo_unitario: parseFloat(metalModalForm.customCusto) || 0
        }
      ]);
    } else {
      const match = ligas.find(l => l.id === metalModalForm.liga_id);
      if (match && match.componentes && match.componentes.length > 0) {
        const expandedItems = match.componentes.map(comp => {
          const matchSupply = supplies.find(s => s.id === comp.insumo_id || s.name === comp.insumo_nome);
          const compWeight = pesoTotal * (Number(comp.percentual) / 100);
          return {
            id: Date.now().toString() + "-" + comp.insumo_id + "-" + Math.random().toString(36).substring(2, 5),
            type: "metal",
            supply_id: comp.insumo_id,
            nome: comp.insumo_nome,
            peso: Math.round(compWeight * 1000) / 1000,
            custo_unitario: matchSupply ? getSupplyUnitCost(matchSupply) : 0,
            multiplicador: parseFloat(multPadraoMetal) || 4.0
          };
        });
        setBomItems(prev => [...prev, ...expandedItems]);
      } else {
        alert("Liga selecionada não possui componentes.");
      }
    }
    setIsMetalModalOpen(false);
  };

  // Load existing Ficha Técnica or Matriz BOM when SKU changes
  useEffect(() => {
    if (loading) return; // Wait until initial data (supplies, ligas) is loaded!
    if (!sku.trim()) return;
    const cleanSku = sku.toUpperCase().trim();
    if (cleanSku === loadedSku) return; // Already loaded this SKU's data!

    let active = true;
    const delayTimer = setTimeout(async () => {
      try {
        // Tenta buscar primeiro no catálogo comercial para preservar as informações corretas
        let catName = "";
        let catModel = "";
        try {
          const catalogItem = await pb.collection("catalog").getFirstListItem(`sku = "${cleanSku}"`);
          if (catalogItem) {
            catName = catalogItem.name || "";
            catModel = catalogItem.full_data?.model || catalogItem.full_data?.modelo || "";
          }
        } catch (e) {
          // não encontrado no catálogo
        }

        // 1. Tenta carregar a Ficha Técnica existente
        const records = await pb.collection("fichas_tecnicas").getList(1, 1, {
          filter: `sku = "${cleanSku}"`
        });

        // 2. Busca na coleção "sku_bom" (Matriz BOM)
        const bomRecords = await pb.collection("sku_bom").getFullList({
          filter: `sku = "${cleanSku}"`,
          expand: "supply_id"
        });

        if (!active) return;

        let existingFicha = null;
        if (records.items.length > 0) {
          existingFicha = records.items[0];
          setProdutoNome(catName || existingFicha.produto_nome || "");
          setModelo(catModel || existingFicha.modelo || "");
          if (existingFicha.custo_operacional !== undefined) setCustoOperacional(String(existingFicha.custo_operacional));
          if (existingFicha.indice_operacional !== undefined) setIndiceOperacional(String(existingFicha.indice_operacional));
          if (existingFicha.indice_financeiro_impostos !== undefined) setIndiceFinanceiro(String(existingFicha.indice_financeiro_impostos));
        } else {
          setProdutoNome(catName || "");
          setModelo(catModel || "");
        }

        if (bomRecords && bomRecords.length > 0) {
          // Se há receita na Matriz BOM, ela manda na composição!
          // Mas tentamos herdar multiplicadores e metais da Ficha Técnica se ela existir
          const savedBomItems = existingFicha?.bom_items || [];
          
          // 1. Mapeia os insumos/pedras da Matriz BOM
          const itemsFromBom = bomRecords.map(r => {
            const supply = r.expand?.supply_id || supplies.find(s => s.id === r.supply_id);
            const isStone = supply?.sector === "pedras";
            const isMetal = supply?.category === "metal" || (supply?.sector === "oficina" && (supply?.name?.includes("Ouro") || supply?.name?.includes("Prata")));
            
            // Procura se esse insumo/metal já existia na Ficha Técnica para herdar seu multiplicador
            const matchingSavedItem = savedBomItems.find(saved => saved.supply_id === r.supply_id);
            const savedMult = matchingSavedItem?.multiplicador;
            
            if (isMetal) {
              return {
                id: r.id,
                type: "metal",
                supply_id: r.supply_id,
                nome: supply?.name || "Metal Desconhecido",
                peso: r.quantity_per_unit || 1,
                custo_unitario: getSupplyUnitCost(supply),
                multiplicador: savedMult !== undefined ? savedMult : (parseFloat(multPadraoMetal) || 4.0)
              };
            }

            return {
              id: r.id,
              type: isStone ? "stone" : "insumo",
              supply_id: r.supply_id,
              nome: supply?.name || "Insumo Desconhecido",
              quantidade: r.quantity_per_unit || 1,
              custo_unitario: getSupplyUnitCost(supply),
              multiplicador: savedMult !== undefined ? savedMult : (parseFloat(isStone ? multPadraoPedra : multPadraoInsumo) || 2.0)
            };
          });

          // 2. Mantém os itens do tipo "metal" salvos na Ficha Técnica que NÃO têm supply_id (customizados)
          const savedMetals = savedBomItems.filter(saved => saved.type === "metal" && !saved.supply_id);
          
          // Combina metais + insumos da Matriz BOM
          setBomItems([...savedMetals, ...itemsFromBom]);
          setLoadedSku(cleanSku);
        } else if (existingFicha) {
          // Se não há Matriz BOM mas há Ficha Técnica, carrega a Ficha Técnica completa
          if (Array.isArray(existingFicha.bom_items)) {
            const updatedItems = existingFicha.bom_items.map(item => {
              if (item.supply_id) {
                const matchedSupply = supplies.find(s => s.id === item.supply_id);
                if (matchedSupply) {
                  return {
                    ...item,
                    custo_unitario: getSupplyUnitCost(matchedSupply) || item.custo_unitario || 0
                  };
                }
              }
              return item;
            });
            setBomItems(updatedItems);
          }
          setLoadedSku(cleanSku);
        } else {
          setBomItems([]);
          setLoadedSku(cleanSku);
        }
      } catch (err) {
        console.error("Erro ao carregar Ficha Técnica ou Matriz BOM:", err);
      }
    }, 500);

    return () => {
      active = false;
      clearTimeout(delayTimer);
    };
  }, [sku, loadedSku, loading, supplies]);

  // Load pending item from modelagem in localStorage
  useEffect(() => {
    const pendingItemStr = localStorage.getItem("pending_precificacao_item");
    const pendingId = localStorage.getItem("pending_precificacao_modeling_id");

    if (pendingItemStr) {
      try {
        const item = JSON.parse(pendingItemStr);
        if (item.sku) setSku(item.sku);
        if (item.nome_produto) setProdutoNome(item.nome_produto);
        if (item.modelo) setModelo(item.modelo);
        if (pendingId) setSourceModelingId(pendingId);

        // Clear localStorage so it doesn't auto-fill on subsequent manual entries
        localStorage.removeItem("pending_precificacao_item");
        localStorage.removeItem("pending_precificacao_modeling_id");
      } catch (err) {
        console.error("Erro ao carregar dados pendentes de modelagem:", err);
      }
    }
  }, []);

  // Fetch initial data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [ligaRecords, supplyRecords] = await Promise.all([
        pb.collection("ligas_receitas").getFullList({ sort: "nome" }),
        pb.collection("supplies").getFullList({ sort: "name" })
      ]);
      setLigas(ligaRecords);
      setSupplies(supplyRecords);

      // Fetch templates record
      try {
        const templatesRecords = await pb.collection("supplies").getFullList({
          filter: 'name = "__bom_templates__"'
        });
        if (templatesRecords.length > 0) {
          const record = templatesRecords[0];
          let props = record.stone_properties;
          if (typeof props === "string") {
            try { props = JSON.parse(props); } catch (e) {}
          }
          setTemplates(props?.templates || {});
        }
      } catch (err) {
        console.error("Erro ao carregar modelos BOM:", err);
      }

      // Check for exported liga from OficinaTab
      const exported = localStorage.getItem("ligas_exported_calc");
      if (exported) {
        try {
          const parsed = JSON.parse(exported);
          const match = ligaRecords.find(l => l.id === parsed.ligaId);
          if (match && match.componentes && match.componentes.length > 0) {
            const totalWeight = 10.0; // default weight is 10g
            const expandedItems = match.componentes.map(comp => {
              const matchSupply = supplyRecords.find(s => s.id === comp.insumo_id || s.name === comp.insumo_nome);
              const compWeight = totalWeight * (Number(comp.percentual) / 100);
              return {
                id: Date.now().toString() + "-" + comp.insumo_id,
                type: "metal",
                supply_id: comp.insumo_id,
                nome: comp.insumo_nome,
                peso: Math.round(compWeight * 1000) / 1000,
                custo_unitario: matchSupply ? getSupplyUnitCost(matchSupply) : 0,
                multiplicador: parseFloat(localStorage.getItem("calc_mult_padrao_metal") || "4.0")
              };
            });
            setBomItems(expandedItems);
          } else {
            // Fallback to custom single metal if recipe not found
            setBomItems([
              {
                id: Date.now().toString(),
                type: "metal",
                nome: parsed.ligaNome,
                liga_id: parsed.ligaId,
                peso: 10,
                multiplicador: parseFloat(localStorage.getItem("calc_mult_padrao_metal") || "4.0"),
                custo_unitario: parsed.custoPorGrama
              }
            ]);
          }
          // Clean up exported liga so it doesn't repeatedly inject on refresh
          localStorage.removeItem("ligas_exported_calc");
        } catch (err) {
          console.error("Erro parsing exported liga:", err);
        }
      }
    } catch (e) {
      console.error("Erro loading price calc data:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Check if SKU exists in the commercial catalogue
  const skuExists = useMemo(() => {
    if (!sku.trim() || !findCatalogItem) return false;
    const item = findCatalogItem(sku.toUpperCase().trim());
    return !!item;
  }, [sku, findCatalogItem]);

  // Filter supplies for stones, metals and others (ignoring templates/configs starting with "_")
  const stonesList = useMemo(() => supplies.filter(s => s.sector === "pedras" && !s.name.startsWith("_")), [supplies]);
  
  const metalsList = useMemo(() => {
    return supplies.filter(s => 
      (s.category === "metal" || (s.sector === "oficina" && (s.name.includes("Ouro") || s.name.includes("Prata")))) &&
      !s.name.startsWith("_")
    );
  }, [supplies]);

  const secondarySuppliesList = useMemo(() => {
    return supplies.filter(s => 
      s.sector !== "pedras" && 
      !(s.category === "metal" || (s.sector === "oficina" && (s.name.includes("Ouro") || s.name.includes("Prata")))) &&
      !s.name.startsWith("_")
    );
  }, [supplies]);

  // Adding BOM items
  const addMetalItem = () => {
    handleOpenMetalModal();
  };

  const addStoneItem = () => {
    const defaultStone = stonesList.length > 0 ? stonesList[0] : null;
    setBomItems(prev => [
      ...prev,
      {
        id: Date.now().toString(),
        type: "stone",
        supply_id: defaultStone ? defaultStone.id : "",
        nome: defaultStone ? defaultStone.name : "",
        quantidade: 1,
        custo_unitario: getSupplyUnitCost(defaultStone)
      }
    ]);
  };

  const addInsumoItem = () => {
    const defaultInsumo = secondarySuppliesList.length > 0 ? secondarySuppliesList[0] : null;
    setBomItems(prev => [
      ...prev,
      {
        id: Date.now().toString(),
        type: "insumo",
        supply_id: defaultInsumo ? defaultInsumo.id : "",
        nome: defaultInsumo ? defaultInsumo.name : "",
        quantidade: 1,
        custo_unitario: getSupplyUnitCost(defaultInsumo)
      }
    ]);
  };

  const removeBomItem = (id) => {
    setBomItems(prev => prev.filter(item => item.id !== id));
  };

  const handleUpdateMetalItem = (id, field, value) => {
    setBomItems(prev => {
      const itemIndex = prev.findIndex(item => item.id === id);
      if (itemIndex === -1) return prev;

      if (field === "liga_id") {
        if (value === "custom") {
          return prev.map(item => item.id === id ? { ...item, liga_id: "custom", nome: "Metal Customizado" } : item);
        }

        const match = ligas.find(l => l.id === value);
        if (match && match.componentes && match.componentes.length > 0) {
          const totalWeight = prev[itemIndex].peso || 1.0;
          const newItems = match.componentes.map(comp => {
            const matchSupply = supplies.find(s => s.id === comp.insumo_id || s.name === comp.insumo_nome);
            const compWeight = totalWeight * (Number(comp.percentual) / 100);
            return {
              id: Date.now().toString() + "-" + comp.insumo_id,
              type: "metal",
              supply_id: comp.insumo_id,
              nome: comp.insumo_nome,
              peso: Math.round(compWeight * 1000) / 1000,
              custo_unitario: matchSupply ? getSupplyUnitCost(matchSupply) : 0,
              multiplicador: parseFloat(multPadraoMetal) || 4.0
            };
          });

          const next = [...prev];
          next.splice(itemIndex, 1, ...newItems);
          return next;
        }
      }

      return prev.map(item => item.id === id ? { ...item, [field]: Number(value) || 0 } : item);
    });
  };

  const handleUpdateSupplyItem = (id, field, value, list) => {
    setBomItems(prev => prev.map(item => {
      if (item.id === id) {
        if (field === "supply_id") {
          const match = list.find(s => s.id === value);
          return {
            ...item,
            supply_id: value,
            nome: match ? match.name : "",
            custo_unitario: getSupplyUnitCost(match)
          };
        }
        return { ...item, [field]: Number(value) || 0 };
      }
      return item;
    }));
  };

  // Calculations
  const calculations = useMemo(() => {
    let custoMetais = 0;
    let custoPedras = 0;
    let custoInsumosBase = 0;

    const detailedItems = bomItems.map(item => {
      let finalCost = 0;
      let formulaStr = "";

      if (item.type === "metal") {
        const mult = item.multiplicador !== undefined ? item.multiplicador : (parseFloat(multPadraoMetal) || 4.0);
        finalCost = item.peso * item.custo_unitario * mult;
        formulaStr = `${item.peso}g x R$ ${formatBRL(item.custo_unitario)} x ${mult}`;
        custoMetais += finalCost;
      } else if (item.type === "stone") {
        // formula: (custo * qtd * mult) + (mult * qtd)
        const mult = item.multiplicador !== undefined ? item.multiplicador : (parseFloat(multPadraoPedra) || 2.0);
        finalCost = (item.custo_unitario * item.quantidade * mult) + (mult * item.quantidade);
        formulaStr = `(R$ ${formatBRL(item.custo_unitario)} x ${item.quantidade} x ${mult}) + (${mult} x ${item.quantidade})`;
        custoPedras += finalCost;
      } else if (item.type === "insumo") {
        // formula: custo * qtd * mult
        const mult = item.multiplicador !== undefined ? item.multiplicador : (parseFloat(multPadraoInsumo) || 2.0);
        finalCost = item.custo_unitario * item.quantidade * mult;
        formulaStr = `R$ ${formatBRL(item.custo_unitario)} x ${item.quantidade} x ${mult}`;
        custoInsumosBase += finalCost;
      }

      return {
        ...item,
        custoCalculado: Math.round(finalCost * 100) / 100,
        formula: formulaStr
      };
    });

    const opCost = parseFloat(custoOperacional) || 0;
    const opIndex = parseFloat(indiceOperacional) || 1.0;
    const finIndex = parseFloat(indiceFinanceiro) || 1.0;

    const custoTotalBase = custoMetais + custoPedras + custoInsumosBase + opCost;
    const precoSugerido = custoTotalBase * opIndex * finIndex;

    return {
      items: detailedItems,
      custoMetais: Math.round(custoMetais * 100) / 100,
      custoPedras: Math.round(custoPedras * 100) / 100,
      custoInsumos: Math.round(custoInsumosBase * 100) / 100,
      custoTotalCalculado: Math.round(custoTotalBase * 100) / 100,
      precoSugerido: Math.round(precoSugerido * 100) / 100
    };
  }, [bomItems, custoOperacional, indiceOperacional, indiceFinanceiro, multPadraoMetal, multPadraoPedra, multPadraoInsumo]);

  const handleApplyDefaultMultipliers = () => {
    setBomItems(prev => prev.map(item => {
      if (item.type === "metal") {
        return { ...item, multiplicador: parseFloat(multPadraoMetal) || 4.0 };
      } else if (item.type === "stone") {
        return { ...item, multiplicador: parseFloat(multPadraoPedra) || 2.0 };
      } else if (item.type === "insumo") {
        return { ...item, multiplicador: parseFloat(multPadraoInsumo) || 2.0 };
      }
      return item;
    }));
    alert("Multiplicadores padrão aplicados a todos os itens da composição!");
  };

  // Save to PocketBase & Pre-register in Catalog
  const handleSaveFicha = async () => {
    if (!sku.trim()) return alert("Digite o SKU do produto.");
    if (!produtoNome.trim()) return alert("Digite o nome do produto.");
    if (bomItems.length === 0) return alert("Adicione pelo menos um item à Ficha Técnica (BOM).");

    const cleanSku = sku.toUpperCase().trim();

    const metalItems = bomItems.filter(i => i.type === "metal");
    const primaryMetal = metalItems.length > 0 ? metalItems[0] : null;

    const payload = {
      sku: cleanSku,
      produto_nome: produtoNome.trim(),
      modelo: modelo.trim(),
      metal_liga_id: primaryMetal ? primaryMetal.liga_id : "",
      metal_liga_nome: primaryMetal ? primaryMetal.nome : "",
      peso_metal: primaryMetal ? primaryMetal.peso : 0,
      multiplicador_metal: primaryMetal ? primaryMetal.multiplicador : (parseFloat(multPadraoMetal) || 4.0),
      bom_items: calculations.items,
      custo_operacional: parseFloat(custoOperacional) || 0,
      indice_operacional: parseFloat(indiceOperacional) || 1.0,
      indice_financeiro_impostos: parseFloat(indiceFinanceiro) || 1.0,
      custo_total_calculado: calculations.custoTotalCalculado,
      preco_sugerido: calculations.precoSugerido
    };

    try {
      // 1. Salvar Ficha Técnica
      await pb.collection("fichas_tecnicas").create(payload);

      // 2. Pré-cadastro ou Atualização no Catálogo Comercial
      let existingRecord = null;
      try {
        existingRecord = await pb.collection("catalog").getFirstListItem(`sku = "${cleanSku}"`);
      } catch (e) {
        // não encontrado
      }

      // Constroi a descrição detalhada (HTML/CSS) com banners
      const descriptionFull = `<style type="text/css">.sj-titulo, .sj-faq-titulo { line-height: 1.3; font-weight: bold; margin-top: 32px; margin-bottom: 16px; } .sj-subtitulo, .sj-faq-pergunta { line-height: 1.3; font-weight: bold; margin-top: 24px; margin-bottom: 12px; } .sj-faq-box h2.sj-faq-titulo { text-align: center; } .sj-texto, .sj-faq-resposta { line-height: 1.5; margin-bottom: 18px; text-align: left; } .sj-link { color: #FFB000; font-weight: bold; text-decoration: underline; transition: color 0.2s; } .sj-link:hover { color: #9B1B1F; } .sj-faq-resposta a { color: #FFB000; font-weight: bold; text-decoration: underline; } .sj-faq-resposta a:hover { color: #9B1B1F; } .sj-whatsapp { color: #075E54 !important; } .sj-faq-box { background-color: #f2f2f2; margin: 32px auto 0 auto; padding: 24px; border-radius: 8px; box-sizing: border-box; } .sj-faq-pergunta { margin-top: 18px; margin-bottom: 6px; } .sj-faq-resposta { margin-bottom: 12px; } .btn-amarelo { display: block; background: #FFCC00; color: #000 !important; padding: 20px; text-align: center; border-radius: 8px; font-weight: bold; text-decoration: none; margin: 20px auto; max-width: 500px; font-size: 18px; box-shadow: 0 4px 8px rgba(0,0,0,0.1); transition: background 0.2s; } .btn-amarelo:hover { background: #e6b800; } .btn-preto { display: inline-block; background: #000; color: #fff !important; padding: 18px 40px; border-radius: 8px; font-weight: bold; text-decoration: none; margin: 30px 0; transition: background 0.2s, color 0.2s; } .btn-preto:hover { background: #FFCC00; color: #000 !important; } .bloco-padrao-sj { border: 1px solid #FFCC00; border-radius: 12px; padding: 32px 50px; text-align: center; margin-bottom: 30px; background: #fff; } .faq-list { display: flex; flex-direction: column; gap: .5rem; width: 100%; text-align: left; } .faq-item { overflow: hidden; transition: all .3s cubic-bezier(.4,0,.2,1); border-bottom: 1px solid #eee; } .faq-question { width: 100%; display: flex; align-items: center; justify-content: justify; padding: 1.25rem 0; background: none; border: none; color: inherit; font-weight: 600; text-align: left; cursor: pointer; font-size: 1.1rem; font-family: 'Poppins', sans-serif; } .faq-question:hover { color: #fcce00; } .faq-question-text { display: flex; align-items: center; gap: .8rem; line-height: 1.4; } .faq-icon-svg { width: 16px; transition: transform .3s; transform: rotate(270deg); fill: #fcce00; flex-shrink: 0; } .faq-question[aria-expanded="true"] .faq-icon-svg { transform: rotate(180deg); } .faq-answer { max-height: 0; overflow: hidden; opacity: 0; transition: all .4s ease; } .faq-answer.is-open { max-height: 800px; opacity: 1; padding-bottom: 20px; } .faq-answer div { font-size: 14px; line-height: 1.6; color: #595959; padding-left: 2.5rem; } .faq-answer div a { color: #FFB000; font-weight: bold; text-decoration: underline; } .faq-answer div a:hover { color: #9B1B1F; } @media(max-width: 768px) { .bloco-padrao-sj { padding: 32px 24px; } .faq-answer div { padding-left: 1.5rem; } }</style>
{{ banners.getById(787) }}
<section class="sj-conteudo">
<h2 class="sj-titulo">${produtoNome.trim()}</h2>

<p class="sj-texto">Levar esta joia junto a você representa sentir a elegância, a durabilidade e o brilho que valorizam o seu visual a cada momento. Desenvolvido com acabamento impecável, este item traz o design ideal para complementar seu estilo com extrema sofisticação.</p>

<div class="sj-texto" style="margin-left: 40px;">- <strong>Material:</strong> Metal nobre com acabamento premium.<br />
- <strong>Design:</strong> Detalhes delicados com acabamento polido, perfeito para todas as ocasiões.<br />
- <strong>Garantia:</strong> Produto com <a class="sj-link" href="https://www.semprejoias.com.br/garantia-sempre-joias/">garantia permanente</a> Sempre Joias.</div>

<h2 class="sj-subtitulo">Experiência Sempre Joias</h2>

<p class="sj-texto">Sua joia chegará em um estojo especial, acompanhada de um certificado de garantia, guia rápido de manutenção e uma flanela mágica para cuidados. E se quiser saber tudo sobre <a class="sj-link" href="https://www.semprejoias.com.br/politica-de-trocas-e-devolucoes/">entregas, prazo, trocas e devoluções</a>, acesse as informações completas. Compre com confiança e eternize este marco! Se tiver dúvidas, nosso atendimento está disponível também pelo <a class="sj-link sj-whatsapp" href="https://api.whatsapp.com/send?phone=5511948335927" target="_blank"><strong>whatsapp</strong></a>.</p>

<div style="text-align:center; margin: 30px 0;"><a class="btn-preto" href="https://www.semprejoias.com.br/">VEJA MAIS JOIAS</a></div>
{{ banners.getById(150) }}

<div style="margin-top: 32px;"></div>

<div class="bloco-padrao-sj">
<h2>Perguntas Frequentes sobre nossa Joia</h2>

<div class="faq-list">
<article class="faq-item"><button aria-controls="ans-1" aria-expanded="false" class="faq-question" id="q-1" type="button"><span class="faq-question-text"> <svg style="fill:#fcce00; margin-right:10px;" viewbox="0 0 512 512" width="18"><path d="M448 0H64C28.7 0 0 28.7 0 64v288c0 35.3 28.7 64 64 64h96v84c0 9.8 11.2 15.5 19.1 9.7L304 416h144c35.3 0 64-28.7 64-64V64c0-35.3-28.7-64-64-64zM352 256H160c-8.8 0-16-7.2-16-16s7.2-16 16-16h192c8.8 0 16 7.2 16 16s7.2 16 16 16zm0-64H160c-8.8 0-16-7.2-16-16s7.2-16 16-16h192c8.8 0 16 7.2 16 16s7.2 16-16 16z"></path></svg> Como cuidar da minha joia? </span> <span class="faq-icon-svg"> <svg style="fill:#fcce00;" viewbox="0 0 448 512" width="14"><path d="M224 416c-8.188 0-16.38-3.125-22.62-9.375l-192-192c-12.5-12.5-12.5-32.75 0-45.25s32.75-12.5 45.25 0L224 338.8l169.4-169.4c12.5-12.5 32.75-12.5 45.25 0s12.5 32.75 0 45.25l-192 192C240.4 412.9 232.2 416 224 416z"></path></svg> </span></button>

<div class="faq-answer" id="ans-1">
<div>Para manter o brilho e a durabilidade da sua joia, evite contato com produtos químicos, retire antes do banho e limpe regularmente com a flanela mágica que acompanha sua joia.</div>
</div>
</article>
</div>
</div>
</section>
<script type="application/ld+json"> { "@context": "https://schema.org", "@type": "FAQPage", "mainEntity": [{"@type":"Question","name":"Como cuidar da minha joia?","acceptedAnswer":{"@type":"Answer","text":"Para manter o brilho e a durabilidade da sua joia, evite contato com produtos químicos, retire antes do banho e limpe regularmente com a flanela mágica que acompanha sua joia."}}] } </script>
<script>
document.querySelectorAll('.faq-question').forEach(function(button){
  button.addEventListener('click',function(){
    var isExpanded = button.getAttribute('aria-expanded') === 'true';
    var answer = button.nextElementSibling;
    document.querySelectorAll('.faq-answer').forEach(function(ans){ ans.classList.remove('is-open'); });
    document.querySelectorAll('.faq-question').forEach(function(btn){ btn.setAttribute('aria-expanded','false'); });
    if(!isExpanded){
      button.setAttribute('aria-expanded','true');
      answer.classList.add('is-open');
    }
  });
});
</script>`;

      const descriptionSmall = `Modelo ${modelo || 'Desconhecido'} em Prata 925 Sempre Joias. Design elegante e discreto para te acompanhar em todos os momentos.`;
      
      const numericId = existingRecord?.full_data?.id || Math.floor(Date.now() / 1000) % 100000;
      
      const catalogFullData = {
        allow_buy: "yes",
        description_full: descriptionFull,
        description_small: descriptionSmall,
        dimensions: JSON.stringify({ length: 16, width: 11, height: 2 }),
        free_shipping: "no",
        gift: "no",
        gift_price: 0,
        group_id: 0,
        handling_days: 0,
        id: numericId,
        image_default: existingRecord?.full_data?.image_default || "",
        images: existingRecord?.full_data?.images || "[]",
        model: modelo.trim(),
        name: produtoNome.trim(),
        ncm: "71131100",
        page_description: descriptionSmall,
        page_keywords: `${modelo?.toLowerCase()}, joia, sempre joias`,
        page_title: `${produtoNome.trim()} | Sempre Joias`,
        points: 0,
        price: calculations.precoSugerido,
        price_gross: 0,
        quantity: existingRecord?.full_data?.quantity ?? 20,
        quantity_critical: 0,
        quantity_max_buy: 0,
        quantity_min_buy: 0,
        sku: cleanSku,
        status: "active",
        stock_method: "disabled",
        tags: JSON.stringify([
          { group: "Tipo de Joia", value: modelo.trim(), hidden: 0, print: 1 },
          { group: "Categoria", value: "Novos Produtos", hidden: 2, print: 0 }
        ]),
        type: "material",
        weight: 0.02
      };

      const catalogPayload = {
        sku: cleanSku,
        name: produtoNome.trim(),
        image_url: existingRecord?.image_url || "",
        full_data: catalogFullData
      };

      if (existingRecord) {
        await pb.collection("catalog").update(existingRecord.id, catalogPayload);
      } else {
        await pb.collection("catalog").create(catalogPayload);
      }

      // Sincroniza a Matriz BOM (sku_bom) com os itens salvos na calculadora
      try {
        const existingBomRecords = await pb.collection("sku_bom").getFullList({
          filter: `sku = "${cleanSku}"`
        });
        await Promise.all(existingBomRecords.map(rec => pb.collection("sku_bom").delete(rec.id)));
        
        const newBomRecords = bomItems.filter(item => item.supply_id);
        await Promise.all(newBomRecords.map(item => 
          pb.collection("sku_bom").create({
            sku: cleanSku,
            supply_id: item.supply_id,
            quantity_per_unit: Number(item.quantidade !== undefined ? item.quantidade : item.peso) || 1
          })
        ));
      } catch (bomErr) {
        console.error("Erro ao sincronizar Matriz BOM (sku_bom):", bomErr);
      }

      // 3. Se originado de uma modelagem, vincula/marca como cadastrado
      if (sourceModelingId) {
        await pb.collection("modelagens").update(sourceModelingId, {
          produto_cadastrado: true,
          sku_catalogo: cleanSku
        });
        setSourceModelingId("");
      }

      alert(`Ficha técnica e Pré-cadastro do SKU ${cleanSku} no catálogo realizados com sucesso!`);
    } catch (err) {
      alert("Erro ao salvar Ficha Técnica e Pré-cadastro: " + err.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8 text-slate-500 bg-slate-50 h-full">
        <div className="w-5 h-5 rounded-full border-2 border-blue-500 border-t-transparent animate-spin mr-2"></div>
        Carregando dados da calculadora...
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col md:flex-row h-full overflow-hidden bg-slate-50 text-xs">
      {/* LEFT: FORM AND BOM BUILDER */}
      <div className="flex-1 p-6 overflow-y-auto custom-scrollbar space-y-6">
        
        {/* HEADER INFORMATION */}
        <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm space-y-4">
          <h2 className="text-sm font-extrabold text-slate-800 flex items-center gap-1.5 border-b border-slate-100 pb-3">
            <Sparkles className="text-purple-500" /> Cadastro de Ficha Técnica & Precificação
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">SKU Desenvolvimento *</label>
              <input
                type="text"
                required
                value={sku}
                onChange={(e) => setSku(e.target.value)}
                placeholder="Ex: AN-TRI-Z-10"
                className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-purple-500 font-mono font-bold"
              />
              {skuExists && (
                <div className="mt-1 text-amber-600 font-bold text-[9px] flex items-center gap-1">
                  <ShieldAlert size={12} /> SKU já existe no catálogo comercial (será atualizado)
                </div>
              )}
            </div>

            <div className="lg:col-span-2">
              <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Nome do Novo Produto *</label>
              <input
                type="text"
                required
                value={produtoNome}
                onChange={(e) => setProdutoNome(e.target.value)}
                placeholder="Ex: Anel Trindade Ouro 18k com Zircônias"
                className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-purple-500 font-bold"
              />
            </div>

            <div>
              <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Modelo (Ex: Escapulário)</label>
              <input
                type="text"
                value={modelo}
                onChange={(e) => setModelo(e.target.value)}
                placeholder="Ex: Escapulário"
                className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-purple-500 font-bold"
              />
            </div>
          </div>

          <div className="border-t border-slate-100 pt-4 space-y-3">
            <div className="flex justify-between items-center">
              <h3 className="text-[9px] font-extrabold text-slate-400 uppercase tracking-wider">Parâmetros Operacionais & Multiplicadores Padrão</h3>
              <button
                type="button"
                onClick={handleApplyDefaultMultipliers}
                className="bg-purple-100 hover:bg-purple-200 text-purple-700 font-bold px-2.5 py-1 rounded text-[9px] uppercase tracking-wide transition-all cursor-pointer border border-purple-200"
              >
                🔄 Atualizar Valores na Composição
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Custo Fixo (R$)</label>
                <input
                  type="number"
                  step="any"
                  value={custoOperacional}
                  onChange={(e) => setCustoOperacional(e.target.value)}
                  className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-purple-500 font-mono font-bold text-slate-700"
                />
              </div>

              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Índice Oper. (Markup)</label>
                <input
                  type="number"
                  step="any"
                  value={indiceOperacional}
                  onChange={(e) => setIndiceOperacional(e.target.value)}
                  className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-purple-500 font-mono font-bold text-slate-700"
                />
              </div>

              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Índice Fin. / Imp.</label>
                <input
                  type="number"
                  step="any"
                  value={indiceFinanceiro}
                  onChange={(e) => setIndiceFinanceiro(e.target.value)}
                  className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-purple-500 font-mono font-bold text-slate-700"
                />
              </div>

              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Mult. Padrão Metal</label>
                <input
                  type="number"
                  step="any"
                  value={multPadraoMetal}
                  onChange={(e) => setMultPadraoMetal(e.target.value)}
                  className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-purple-500 font-mono font-bold text-slate-700"
                />
              </div>

              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Mult. Padrão Pedras</label>
                <input
                  type="number"
                  step="any"
                  value={multPadraoPedra}
                  onChange={(e) => setMultPadraoPedra(e.target.value)}
                  className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-purple-500 font-mono font-bold text-slate-700"
                />
              </div>

              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Mult. Padrão Insumos</label>
                <input
                  type="number"
                  step="any"
                  value={multPadraoInsumo}
                  onChange={(e) => setMultPadraoInsumo(e.target.value)}
                  className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-purple-500 font-mono font-bold text-slate-700"
                />
              </div>
            </div>
          </div>
        </div>

        {/* BOM ITEMS BUILDER */}
        <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm space-y-4">
          <div className="flex justify-between items-center border-b border-slate-100 pb-3">
            <div>
              <h3 className="text-sm font-bold text-slate-800">Composição do Produto (BOM)</h3>
              <p className="text-[10px] text-slate-400">Insira ligas de metal, pedras e insumos para construir o custo.</p>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setIsRecipeModalOpen(true)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-3 py-1.5 rounded-lg flex items-center gap-1 shadow-sm transition-all"
              >
                <Layers size={14} /> + Receita/BOM
              </button>
              <button
                type="button"
                onClick={addMetalItem}
                className="bg-purple-600 hover:bg-purple-750 text-white font-bold px-3 py-1.5 rounded-lg flex items-center gap-1 shadow-sm transition-all"
              >
                + Metal (Liga)
              </button>
              <button
                type="button"
                onClick={addStoneItem}
                className="bg-blue-600 hover:bg-blue-750 text-white font-bold px-3 py-1.5 rounded-lg flex items-center gap-1 shadow-sm transition-all"
              >
                + Pedra
              </button>
              <button
                type="button"
                onClick={addInsumoItem}
                className="bg-slate-700 hover:bg-slate-800 text-white font-bold px-3 py-1.5 rounded-lg flex items-center gap-1 shadow-sm transition-all"
              >
                + Insumo
              </button>
            </div>
          </div>

          <div className="space-y-3">
            {calculations.items.map((item, idx) => (
              <div key={item.id} className="p-4 rounded-xl border border-slate-200 bg-slate-50/50 flex flex-col md:flex-row md:items-center justify-between gap-4">
                
                {/* Item Type & Selector */}
                <div className="flex-1 grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
                  <div className="md:col-span-3">
                    <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-wider block text-center ${
                      item.type === "metal"
                        ? "bg-purple-100 text-purple-800 border border-purple-200"
                        : item.type === "stone"
                        ? "bg-blue-100 text-blue-800 border border-blue-200"
                        : "bg-slate-100 text-slate-850 border border-slate-200"
                    }`}>
                      {item.type}
                    </span>
                  </div>

                  <div className="md:col-span-9 font-bold">
                    {item.type === "metal" ? (
                      item.supply_id ? (
                        <select
                          value={item.supply_id}
                          onChange={(e) => handleUpdateSupplyItem(item.id, "supply_id", e.target.value, metalsList)}
                          className="w-full p-2 border border-slate-355 rounded-lg bg-white"
                        >
                          <option value="" disabled>Selecione o metal...</option>
                          {metalsList.map(s => (
                            <option key={s.id} value={s.id}>{s.name} (R$ {formatBRL(getSupplyUnitCost(s))}/g)</option>
                          ))}
                        </select>
                      ) : (
                        <select
                          value={item.liga_id || ""}
                          onChange={(e) => handleUpdateMetalItem(item.id, "liga_id", e.target.value)}
                          className="w-full p-2 border border-slate-355 rounded-lg bg-white"
                        >
                          <option value="" disabled>Selecione a liga...</option>
                          {ligas.map(l => (
                            <option key={l.id} value={l.id}>{l.nome}</option>
                          ))}
                          <option value="custom">Metal Customizado</option>
                        </select>
                      )
                    ) : item.type === "stone" ? (
                      <select
                        value={item.supply_id}
                        onChange={(e) => handleUpdateSupplyItem(item.id, "supply_id", e.target.value, stonesList)}
                        className="w-full p-2 border border-slate-350 rounded-lg bg-white"
                      >
                        <option value="" disabled>Selecione a pedra...</option>
                        {stonesList.map(s => (
                          <option key={s.id} value={s.id}>{s.name} (R$ {formatBRL(getSupplyUnitCost(s))}/un)</option>
                        ))}
                      </select>
                    ) : (
                      <select
                        value={item.supply_id}
                        onChange={(e) => handleUpdateSupplyItem(item.id, "supply_id", e.target.value, secondarySuppliesList)}
                        className="w-full p-2 border border-slate-350 rounded-lg bg-white"
                      >
                        <option value="" disabled>Selecione o insumo...</option>
                        {secondarySuppliesList.map(s => (
                          <option key={s.id} value={s.id}>{s.name} (R$ {formatBRL(getSupplyUnitCost(s))}/{s.unit})</option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>

                {/* Quantitative inputs */}
                <div className="flex items-center gap-3 md:w-80 shrink-0">
                  {item.type === "metal" ? (
                    <>
                      <div className="w-24">
                        <label className="block text-[9px] font-bold text-slate-400 uppercase mb-0.5">Peso (g)</label>
                        <input
                          type="number"
                          step="any"
                          value={item.peso}
                          onChange={(e) => handleUpdateMetalItem(item.id, "peso", e.target.value)}
                          className="w-full p-1.5 border border-slate-300 rounded font-mono font-bold text-center"
                        />
                      </div>
                      <div className="w-24">
                        <label className="block text-[9px] font-bold text-slate-400 uppercase mb-0.5">Custo/g (R$)</label>
                        <input
                          type="number"
                          step="any"
                          value={item.custo_unitario}
                          onChange={(e) => handleUpdateMetalItem(item.id, "custo_unitario", e.target.value)}
                          className="w-full p-1.5 border border-slate-300 rounded font-mono font-bold text-center"
                        />
                      </div>
                      <div className="w-20">
                        <label className="block text-[9px] font-bold text-slate-400 uppercase mb-0.5">Mult.</label>
                        <input
                          type="number"
                          step="any"
                          value={item.multiplicador !== undefined ? item.multiplicador : (parseFloat(multPadraoMetal) || 4.0)}
                          onChange={(e) => handleUpdateMetalItem(item.id, "multiplicador", e.target.value)}
                          className="w-full p-1.5 border border-slate-300 rounded font-mono font-bold text-center"
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="w-24">
                        <label className="block text-[9px] font-bold text-slate-400 uppercase mb-0.5">Quantidade</label>
                        <input
                          type="number"
                          value={item.quantidade}
                          onChange={(e) => handleUpdateSupplyItem(item.id, "quantidade", e.target.value, supplies)}
                          className="w-full p-1.5 border border-slate-300 rounded font-mono font-bold text-center"
                        />
                      </div>
                      <div className="w-24">
                        <label className="block text-[9px] font-bold text-slate-400 uppercase mb-0.5">Custo Unit (R$)</label>
                        <input
                          type="number"
                          step="any"
                          value={item.custo_unitario}
                          onChange={(e) => handleUpdateSupplyItem(item.id, "custo_unitario", e.target.value, supplies)}
                          className="w-full p-1.5 border border-slate-300 rounded font-mono font-bold text-center"
                        />
                      </div>
                      <div className="w-20">
                        <label className="block text-[9px] font-bold text-slate-400 uppercase mb-0.5">Mult.</label>
                        <input
                          type="number"
                          step="any"
                          value={item.multiplicador !== undefined ? item.multiplicador : (item.type === "stone" ? multPadraoPedra : multPadraoInsumo)}
                          onChange={(e) => handleUpdateSupplyItem(item.id, "multiplicador", e.target.value, supplies)}
                          className="w-full p-1.5 border border-slate-300 rounded font-mono font-bold text-center"
                        />
                      </div>
                    </>
                  )}

                  <button
                    type="button"
                    onClick={() => removeBomItem(item.id)}
                    className="p-1.5 bg-red-50 text-red-600 border border-red-200 rounded hover:bg-red-100 self-end transition-colors"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>

              </div>
            ))}

            {bomItems.length === 0 && (
              <div className="p-8 text-center text-slate-400 border border-dashed border-slate-300 rounded-xl">
                Nenhum componente inserido na Ficha Técnica (BOM) ainda.
              </div>
            )}
          </div>
        </div>

      </div>

      {/* RIGHT: COST AND PRICING CALCULATOR SUMMARY */}
      <div className="w-full md:w-96 border-t md:border-t-0 md:border-l border-slate-200 bg-white flex flex-col shrink-0 p-6 overflow-y-auto custom-scrollbar gap-6">
        <div>
          <h3 className="font-extrabold text-slate-700 text-sm border-b border-slate-100 pb-3 flex items-center gap-1.5">
            <Database size={16} className="text-purple-500" /> Resumo do Cálculo
          </h3>
        </div>

        <div className="space-y-4">
          <div className="p-4 rounded-xl border border-slate-200 bg-slate-50/50 space-y-2">
            <div className="flex justify-between items-center text-slate-500 font-bold">
              <span>Custo Metais (Ligas):</span>
              <span className="font-mono text-slate-800">R$ {formatBRL(calculations.custoMetais)}</span>
            </div>
            <div className="flex justify-between items-center text-slate-500 font-bold">
              <span>Custo Pedras (Zircônias):</span>
              <span className="font-mono text-slate-800">R$ {formatBRL(calculations.custoPedras)}</span>
            </div>
            <div className="flex justify-between items-center text-slate-500 font-bold">
              <span>Custo Insumos Embalag.:</span>
              <span className="font-mono text-slate-800">R$ {formatBRL(calculations.custoInsumos)}</span>
            </div>
            <div className="flex justify-between items-center text-slate-500 font-bold">
              <span>Custo Operacional Fixo:</span>
              <span className="font-mono text-slate-800">R$ {formatBRL(parseFloat(custoOperacional || 0))}</span>
            </div>

            <div className="flex justify-between items-center text-slate-850 font-black pt-2 border-t border-slate-200 text-xs">
              <span>Custo Total de Produção:</span>
              <span className="font-mono text-purple-700 text-sm">R$ {formatBRL(calculations.custoTotalCalculado)}</span>
            </div>
          </div>

          <div className="p-4 rounded-xl bg-purple-900 text-white space-y-2 border border-purple-950 shadow-md">
            <div className="flex justify-between text-purple-200 font-bold text-[10px] uppercase tracking-wider">
              <span>Fórmula aplicada</span>
              <span>Markup x Impostos</span>
            </div>
            <div className="text-[10px] text-purple-200 font-mono italic">
              Base (R$ {formatBRL(calculations.custoTotalCalculado)}) x Markup ({indiceOperacional}) x Financ ({indiceFinanceiro})
            </div>
            <div className="pt-2 border-t border-purple-800 flex justify-between items-end">
              <span className="font-black text-xs uppercase tracking-wide">Preço Sugerido:</span>
              <span className="font-black font-mono text-lg text-yellow-300">R$ {formatBRL(calculations.precoSugerido)}</span>
            </div>
          </div>
        </div>

        {/* Diffs/Formulas reference details */}
        <div className="bg-slate-50 rounded-xl border border-slate-200 p-4 space-y-2">
          <h4 className="font-bold text-[10px] text-slate-400 uppercase tracking-wider">Fórmulas Específicas do Sistema</h4>
          <ul className="list-disc pl-4 space-y-1 text-slate-500 font-semibold leading-relaxed">
            <li><strong>Pedras:</strong> <span className="font-mono text-[10px]">(Custo x Qtd x {multPadraoPedra}) + {multPadraoPedra} x Qtd</span></li>
            <li><strong>Insumos:</strong> <span className="font-mono text-[10px]">Custo x Qtd x {multPadraoInsumo}</span></li>
            <li><strong>Metal:</strong> <span className="font-mono text-[10px]">Peso x Custo/g x {multPadraoMetal}</span></li>
          </ul>
        </div>

        <button
          onClick={handleSaveFicha}
          disabled={bomItems.length === 0}
          className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold p-3.5 rounded-xl transition-all shadow-md flex items-center justify-center gap-2 text-xs uppercase tracking-wider disabled:opacity-50"
        >
          <Save size={16} /> {skuExists ? "Atualizar Ficha & Catálogo" : "Salvar Ficha Técnica"}
        </button>

        <button
          onClick={() => {
            if (window.confirm("Deseja limpar todos os campos da calculadora?")) {
              setSku("");
              setProdutoNome("");
              setModelo("");
              setBomItems([]);
              setCustoOperacional("10.00");
              setIndiceOperacional("1.5");
              setIndiceFinanceiro("1.2");
              setSourceModelingId("");
              setLoadedSku("");
            }
          }}
          className="w-full bg-slate-100 hover:bg-slate-200 text-slate-650 font-bold p-2 rounded-xl transition-all border border-slate-250 flex items-center justify-center gap-2 text-[10px] uppercase tracking-wider mt-2 select-none"
        >
          Limpar Formulário
        </button>

      </div>

      {/* METAL / LIGA MODAL */}
      {isMetalModalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[70] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full shadow-2xl border border-slate-200 flex flex-col max-h-[85vh] animate-scale-up">
            <div className="p-5 border-b border-slate-100 flex justify-between items-center shrink-0">
              <h3 className="text-sm font-bold text-slate-800">Adicionar Metal / Liga</h3>
              <button type="button" onClick={() => setIsMetalModalOpen(false)} className="text-slate-400 hover:text-slate-600 font-bold">✕</button>
            </div>

            <form onSubmit={handleConfirmMetalModal} className="p-6 space-y-4 font-bold text-xs">
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Selecione a Liga *</label>
                <select
                  value={metalModalForm.liga_id}
                  onChange={(e) => setMetalModalForm({ ...metalModalForm, liga_id: e.target.value })}
                  className="w-full p-2.5 border border-slate-300 rounded-lg bg-white outline-none focus:ring-1 focus:ring-purple-500 text-xs font-bold text-slate-700 cursor-pointer"
                >
                  {ligas.map(l => (
                    <option key={l.id} value={l.id}>{l.nome}</option>
                  ))}
                  <option value="custom">Metal Customizado (Manual)</option>
                </select>
              </div>

              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Peso Total da Peça (g) *</label>
                <input
                  type="number"
                  step="any"
                  required
                  value={metalModalForm.peso}
                  onChange={(e) => setMetalModalForm({ ...metalModalForm, peso: e.target.value })}
                  className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-purple-500 font-mono font-bold"
                />
              </div>

              {metalModalForm.liga_id === "custom" && (
                <div className="space-y-4 p-3 bg-slate-50 rounded-xl border border-slate-200">
                  <div>
                    <label className="block text-[9px] font-bold text-slate-500 uppercase tracking-wider mb-1">Nome do Metal Customizado</label>
                    <input
                      type="text"
                      value={metalModalForm.customNome}
                      onChange={(e) => setMetalModalForm({ ...metalModalForm, customNome: e.target.value })}
                      className="w-full p-2 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-purple-500"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[9px] font-bold text-slate-500 uppercase tracking-wider mb-1">Custo/g (R$)</label>
                      <input
                        type="number"
                        step="any"
                        value={metalModalForm.customCusto}
                        onChange={(e) => setMetalModalForm({ ...metalModalForm, customCusto: e.target.value })}
                        className="w-full p-2 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-purple-500 font-mono font-bold text-slate-700"
                      />
                    </div>
                    <div>
                      <label className="block text-[9px] font-bold text-slate-500 uppercase tracking-wider mb-1">Multiplicador</label>
                      <input
                        type="number"
                        step="any"
                        value={metalModalForm.customMult}
                        onChange={(e) => setMetalModalForm({ ...metalModalForm, customMult: e.target.value })}
                        className="w-full p-2 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-purple-500 font-mono font-bold text-slate-700"
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="pt-4 border-t border-slate-100 flex justify-end gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => setIsMetalModalOpen(false)}
                  className="px-4 py-2 text-xs font-bold text-slate-500 hover:bg-slate-100 rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-purple-600 hover:bg-purple-750 text-white font-bold rounded-lg text-xs shadow-sm"
                >
                  Confirmar e Decompor
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* RECIPE / BOM TEMPLATE MODAL */}
      {isRecipeModalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[70] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full shadow-2xl border border-slate-200 flex flex-col max-h-[85vh] animate-scale-up">
            <div className="p-5 border-b border-slate-100 flex justify-between items-center shrink-0">
              <h3 className="text-sm font-bold text-slate-800">Aplicar Receita BOM Pré-cadastrada</h3>
              <button type="button" onClick={() => setIsRecipeModalOpen(false)} className="text-slate-400 hover:text-slate-600 font-bold">✕</button>
            </div>

            <form onSubmit={handleApplyRecipeTemplate} className="p-6 space-y-4 font-bold text-xs">
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Selecione a Receita *</label>
                <select
                  value={selectedRecipeName}
                  onChange={(e) => setSelectedRecipeName(e.target.value)}
                  className="w-full p-2.5 border border-slate-300 rounded-lg bg-white outline-none focus:ring-1 focus:ring-purple-500 text-xs font-bold text-slate-700 cursor-pointer"
                >
                  <option value="" disabled>Escolha uma receita...</option>
                  {Object.keys(templates).map(name => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-2 py-2">
                <input
                  type="checkbox"
                  id="clear-existing-recipe"
                  checked={clearExistingOnApply}
                  onChange={(e) => setClearExistingOnApply(e.target.checked)}
                  className="w-4 h-4 text-purple-650 border-slate-300 rounded focus:ring-purple-500 cursor-pointer"
                />
                <label htmlFor="clear-existing-recipe" className="text-[11px] font-bold text-slate-700 cursor-pointer select-none">
                  Substituir itens atuais (zerar composição atual)
                </label>
              </div>

              <div className="pt-4 border-t border-slate-100 flex justify-end gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => setIsRecipeModalOpen(false)}
                  className="px-4 py-2 text-xs font-bold text-slate-500 hover:bg-slate-100 rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-lg text-xs shadow-sm"
                >
                  Aplicar Receita
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
